import React, { useCallback, useEffect, useRef, useState } from 'react'
import { supabase } from '../supabaseClient'
import { resumoInboxVendedor } from '../services/whatsappCrmApi'
import WhatsAppCrm from './WhatsAppCrm'
import { useIntervaloVisivel } from '../hooks/useIntervaloVisivel'
import { desbloquearAvisoWhatsApp, tocarAvisoWhatsApp } from '../utils/whatsappAvisoSonoro'
import 'bootstrap-icons/font/bootstrap-icons.css'
import './WhatsAppFloatingChat.css'

const WhatsAppFloatingChat = ({ userId, userRole, chatZap, ocultar = false }) => {
  const [aberto, setAberto] = useState(false)
  const [totalNaoLidas, setTotalNaoLidas] = useState(0)
  const [conversaDestaqueId, setConversaDestaqueId] = useState(null)
  const [conversaParaAbrir, setConversaParaAbrir] = useState(null)
  const [refreshNonce, setRefreshNonce] = useState(0)
  const [temNotificacaoNova, setTemNotificacaoNova] = useState(false)
  const conversaNotificadaRef = useRef(null)
  const painelAbertoRef = useRef(false)
  const naoLidasAnteriorRef = useRef(null)

  const atualizarResumo = useCallback(async () => {
    if (!userId) return
    const res = await resumoInboxVendedor(userId)
    if (!res.success) return
    if (
      naoLidasAnteriorRef.current != null
      && res.totalNaoLidas > naoLidasAnteriorRef.current
    ) {
      tocarAvisoWhatsApp()
    }
    naoLidasAnteriorRef.current = res.totalNaoLidas
    setTotalNaoLidas(res.totalNaoLidas)
    setConversaDestaqueId(res.conversaDestaqueId)
    if (res.totalNaoLidas === 0) {
      setTemNotificacaoNova(false)
      conversaNotificadaRef.current = null
      return
    }
    conversaNotificadaRef.current = res.conversaDestaqueId
    if (!painelAbertoRef.current) {
      setTemNotificacaoNova(true)
    }
  }, [userId])

  useEffect(() => {
    atualizarResumo()
  }, [atualizarResumo])

  useIntervaloVisivel(atualizarResumo, 5000)

  useEffect(() => {
    painelAbertoRef.current = aberto
  }, [aberto])

  useEffect(() => {
    const desbloquear = () => desbloquearAvisoWhatsApp()
    document.addEventListener('pointerdown', desbloquear, { passive: true })
    document.addEventListener('keydown', desbloquear)
    return () => {
      document.removeEventListener('pointerdown', desbloquear)
      document.removeEventListener('keydown', desbloquear)
    }
  }, [])

  useEffect(() => {
    if (!userId) return undefined

    const inboundDoVendedor = async (msg) => {
      if (!msg || String(msg.direction || '').toLowerCase() !== 'inbound') return false
      if (msg.vendedor_id && String(msg.vendedor_id) === String(userId)) return true
      if (!msg.conversa_id) return false
      const { data } = await supabase
        .from('whatsapp_conversas')
        .select('vendedor_id')
        .eq('id', msg.conversa_id)
        .maybeSingle()
      return data && String(data.vendedor_id) === String(userId)
    }

    const channel = supabase
      .channel(`whatsapp-float-${userId}`)
      .on(
        'postgres_changes',
        { event: '*', schema: 'public', table: 'whatsapp_conversas' },
        () => { atualizarResumo() }
      )
      .on(
        'postgres_changes',
        { event: '*', schema: 'public', table: 'whatsapp_mensagens' },
        async (payload) => {
          const tipo = payload?.eventType || payload?.event
          if (tipo === 'INSERT' && await inboundDoVendedor(payload?.new)) {
            tocarAvisoWhatsApp()
          }
          atualizarResumo()
        }
      )
      .subscribe()

    return () => {
      supabase.removeChannel(channel)
    }
  }, [userId, atualizarResumo])

  const fecharPainel = () => {
    setAberto(false)
    setTemNotificacaoNova(false)
    atualizarResumo()
  }

  const handleToggle = () => {
    desbloquearAvisoWhatsApp()
    if (aberto) {
      fecharPainel()
      return
    }
    const idParaAbrir = conversaNotificadaRef.current || conversaDestaqueId
    setConversaParaAbrir(idParaAbrir || null)
    setRefreshNonce((n) => n + 1)
    setTemNotificacaoNova(false)
    setAberto(true)
  }

  if (!userId || Number(chatZap) !== 1) return null

  const badge = totalNaoLidas > 99 ? '99+' : String(totalNaoLidas)

  return (
    <div className={`wa-float${ocultar ? ' wa-float--hidden' : ''}${aberto ? ' wa-float--open' : ''}`}>
      {aberto && (
        <div className="wa-float-panel" role="dialog" aria-label="Conversas do WhatsApp">
          <div className="wa-float-panel-bar">
            <span className="wa-float-panel-title">
              <i className="bi bi-whatsapp" aria-hidden />
              WhatsApp
            </span>
            <button
              type="button"
              className="wa-float-panel-close"
              onClick={fecharPainel}
              title="Fechar"
              aria-label="Fechar conversas"
            >
              <i className="bi bi-x-lg" />
            </button>
          </div>
          <div className="wa-float-panel-body">
            <WhatsAppCrm
              userRole={userRole}
              userId={userId}
              chatZap={chatZap}
              conversaInicialId={conversaParaAbrir}
              refreshNonce={refreshNonce}
              modoWidget
            />
          </div>
        </div>
      )}

      <button
        type="button"
        className={`wa-float-btn${temNotificacaoNova || totalNaoLidas > 0 ? ' wa-float-btn--alert' : ''}`}
        onClick={handleToggle}
        title={aberto ? 'Fechar WhatsApp' : 'Abrir conversas do WhatsApp'}
        aria-label={aberto ? 'Fechar WhatsApp' : 'Abrir conversas do WhatsApp'}
      >
        <i className={`bi ${aberto ? 'bi-x-lg' : 'bi-whatsapp'}`} aria-hidden />
        {!aberto && totalNaoLidas > 0 && (
          <span className="wa-float-badge">{badge}</span>
        )}
      </button>
    </div>
  )
}

export default WhatsAppFloatingChat
